// In-app notification bell. Lives in the top bar to the left of the
// theme toggle. Polls /api/notifications/list every 15 s. Shows an
// unread badge; clicking opens a dropdown with the most recent items.
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, FileText, AlertCircle, Sparkles, Inbox } from 'lucide-react';
import { apiFetch } from '../../fetchInterceptor';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from '../ui/dropdown-menu';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 15000;

const TYPE_ICON = {
  ocr_scan_complete: FileText,
  ocr_scan_failed: AlertCircle,
  tax_savings_ready: Sparkles,
  engagement_letter_signed: Check,
  generic: Bell,
};

const TYPE_COLOR = {
  ocr_scan_complete: 'text-emerald-600',
  ocr_scan_failed: 'text-rose-600',
  tax_savings_ready: 'text-violet-600',
  engagement_letter_signed: 'text-blue-600',
  generic: 'text-muted-foreground',
};

const relTime = (iso) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

export const NotificationBell = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await apiFetch(`${API_URL}/api/notifications/list?limit=15`);
      if (!r.ok) return;
      const data = await r.json();
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch {
      // silent — bell never blocks anything
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    // Also refresh when window regains focus — gives users a fresh
    // unread count the moment they switch back to the tab.
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [load]);

  const openItem = async (n) => {
    setOpen(false);
    if (!n.read) {
      try {
        await apiFetch(`${API_URL}/api/notifications/${n.notification_id}/read`, { method: 'POST' });
      } catch {
        // swallow — best effort
      }
    }
    if (n.link) {
      navigate(n.link);
    }
    load();
  };

  const markAllRead = async () => {
    try {
      await apiFetch(`${API_URL}/api/notifications/read-all`, { method: 'POST' });
    } catch {
      // swallow
    }
    load();
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          data-testid="topbar-notification-bell"
          aria-label="Notifications"
        >
          <Bell className="w-5 h-5 text-muted-foreground" />
          {unread > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 text-[10px] font-bold flex items-center justify-center rounded-full"
              data-testid="notification-unread-badge"
            >
              {unread > 9 ? '9+' : unread}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between p-3 border-b">
          <div className="font-semibold text-sm">Notifications</div>
          {unread > 0 && (
            <button
              type="button"
              onClick={markAllRead}
              className="text-xs text-violet-600 hover:underline"
              data-testid="notification-mark-all-read"
            >
              Mark all read
            </button>
          )}
        </div>
        <div className="max-h-[420px] overflow-y-auto" data-testid="notification-list">
          {items.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground" data-testid="notification-empty">
              <Inbox className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <div>You&apos;re all caught up.</div>
              <div className="text-xs mt-1 opacity-70">We&apos;ll ping you here when scans finish.</div>
            </div>
          ) : (
            items.map((n) => {
              const Icon = TYPE_ICON[n.type] || Bell;
              const color = TYPE_COLOR[n.type] || 'text-muted-foreground';
              return (
                <button
                  key={n.notification_id}
                  type="button"
                  onClick={() => openItem(n)}
                  className={cn(
                    'w-full text-left p-3 border-b last:border-b-0 hover:bg-muted/50 flex items-start gap-3 transition-colors',
                    !n.read && 'bg-violet-50/40 dark:bg-violet-950/20'
                  )}
                  data-testid={`notification-item-${n.notification_id}`}
                >
                  <div className={cn('mt-0.5 shrink-0', color)}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium text-sm truncate">{n.title}</div>
                      {!n.read && (
                        <span className="w-1.5 h-1.5 rounded-full bg-violet-600 shrink-0" />
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                      {n.message && n.message.length > 90
                        ? `${n.message.slice(0, 70)}…${n.message.slice(-15)}`
                        : n.message}
                    </div>
                    <div className="text-[10px] text-muted-foreground/70 mt-1">
                      {relTime(n.created_at)}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
